import React, { useState, useEffect } from 'react';
import { useCustomers, useQuotations, useUsers, useSales, useBatteryStock, useChargerStock } from '../hooks/useDatabase';
import SaleDetailsModal from './SaleDetailsModal';
import QuotationDetailsModal from './QuotationDetailsModal';

const QuotationPage = ({ currentUser }) => {
  const { customers, loading: customersLoading } = useCustomers();
  const { quotations, createQuotation, fetchQuotations, deleteQuotation, loading: quotationsLoading } = useQuotations();
  const { users, loading: usersLoading } = useUsers();
  const { sales, createSale, loading: salesLoading } = useSales();
  const { batteryStock, fetchBatteryStock, loading: batteryLoading } = useBatteryStock();
  const { chargerStock, fetchChargerStock, loading: chargerLoading } = useChargerStock();

  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [quotationProducts, setQuotationProducts] = useState([]);
  const [quotationNotes, setQuotationNotes] = useState('');
  const [discount, setDiscount] = useState(0);
  const [customerEmail, setCustomerEmail] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [salesPerson, setSalesPerson] = useState('');
  const [salesPhone, setSalesPhone] = useState('');
  const [salesEmail, setSalesEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('30 วัน');
  const [paymentTerms, setPaymentTerms] = useState('มัดจำ 50 % วันส่งมือ ชำระส่วนที่เหลือส่งงาน');
  const [serviceTerms, setServiceTerms] = useState('รับประกัน แบตเตอรี่ 36 เดือน รับประกัน ชาร์จเจอร์ 12 เดือน รับประกัน งานติดตั้ง 7 วัน');
  const [showSalePopup, setShowSalePopup] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [selectedSerials, setSelectedSerials] = useState({});
  const [isProcessingSale, setIsProcessingSale] = useState(false);
  const [warrantyPeriods, setWarrantyPeriods] = useState({});
  const [warrantyErrors, setWarrantyErrors] = useState({});
  const [pmServiceCounts, setPmServiceCounts] = useState({});
  const [pmServiceErrors, setPmServiceErrors] = useState({});
  const [saleDeliveryDate, setSaleDeliveryDate] = useState('');
  const [showStockSelectionModal, setShowStockSelectionModal] = useState(false);
  const [stockSelectionData, setStockSelectionData] = useState({
    productIndex: null,
    selectedStockItems: [],
    searchTerm: '',
    filterType: 'all',
    filterBrand: '',
    filterCapacity: ''
  });
  const [showSaleConfirmation, setShowSaleConfirmation] = useState(false);
  const [saleConfirmationData, setSaleConfirmationData] = useState(null);
  const [showSaleDetailsModal, setShowSaleDetailsModal] = useState(false);
  const [selectedSaleForDetails, setSelectedSaleForDetails] = useState(null);
  const [showQuotationDetailsModal, setShowQuotationDetailsModal] = useState(false);
  const [selectedQuotationForDetails, setSelectedQuotationForDetails] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedQuotations = React.useMemo(() => {
    let sortableItems = [...quotations];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle specific keys
        if (sortConfig.key === 'quotationNumber') {
          // Extract number part for correct sorting if needed, but string sort usually works for formatted IDs
        } else if (sortConfig.key === 'totalAmount') {
          aValue = parseFloat(a.totalAmount || 0);
          bValue = parseFloat(b.totalAmount || 0);
        } else if (sortConfig.key === 'created_at') {
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [quotations, sortConfig]);

  // Auto-fill sales person information from current user
  useEffect(() => {
    if (currentUser) {
      setSalesPerson(currentUser.name || currentUser.username || '');
      setSalesPhone(currentUser.phone || '');
      setSalesEmail(currentUser.email || '');
    }
  }, [currentUser]);

  const openQuotationForm = () => {
    setShowQuotationForm(true);
    setIsEditMode(false);
    setEditingQuotation(null);
    setSelectedCustomer('');
    setQuotationProducts([]);
    setQuotationNotes('');
    setDiscount(0);
    // Keep sales person information from current user
    if (currentUser) {
      setSalesPerson(currentUser.name || currentUser.username || '');
      setSalesPhone(currentUser.phone || '');
      setSalesEmail(currentUser.email || '');
    }
  };

  const closeQuotationForm = () => {
    setShowQuotationForm(false);
  };

  const addProduct = () => {
    setQuotationProducts([...quotationProducts, {
      type: 'การบริการ',
      description: '',
      quantity: 1,
      price: 0,
      stockItems: []
    }]);
  };

  const openStockSelectionModal = (productIndex) => {
    setStockSelectionData({
      productIndex,
      selectedStockItems: quotationProducts[productIndex]?.stockItems || [],
      searchTerm: '',
      filterType: 'all',
      filterBrand: '',
      filterCapacity: ''
    });
    setShowStockSelectionModal(true);
  };

  const closeStockSelectionModal = () => {
    setShowStockSelectionModal(false);
    setStockSelectionData({
      productIndex: null,
      selectedStockItems: [],
      searchTerm: '',
      filterType: 'all',
      filterBrand: '',
      filterCapacity: ''
    });
  };

  const getFilteredStock = () => {
    const allStock = [
      ...batteryStock.filter(item => item.status === 'พร้อมขาย').map(item => ({ ...item, type: 'battery' })),
      ...chargerStock.filter(item => item.status === 'พร้อมขาย').map(item => ({ ...item, type: 'charger' }))
    ];

    // กรองตามเงื่อนไขค้นหาและฟิลเตอร์
    const filtered = allStock.filter(item => {
      const matchesSearch = !stockSelectionData.searchTerm ||
        item.serial?.toLowerCase().includes(stockSelectionData.searchTerm.toLowerCase()) ||
        item.brand?.toLowerCase().includes(stockSelectionData.searchTerm.toLowerCase()) ||
        item.capacity?.toLowerCase().includes(stockSelectionData.searchTerm.toLowerCase());

      const matchesType = stockSelectionData.filterType === 'all' || item.type === stockSelectionData.filterType;
      const matchesBrand = !stockSelectionData.filterBrand || item.brand === stockSelectionData.filterBrand;
      const matchesCapacity = !stockSelectionData.filterCapacity || item.capacity === stockSelectionData.filterCapacity;

      return matchesSearch && matchesType && matchesBrand && matchesCapacity;
    });

    // จัดกลุ่มตามประเภท ยี่ห้อ และความจุ แล้วเลือกเพียง 1 รายการต่อกลุ่ม
    const groupedStock = {};
    filtered.forEach(item => {
      const key = `${item.type}-${item.brand}-${item.capacity}`;
      if (!groupedStock[key]) {
        groupedStock[key] = {
          ...item,
          availableCount: 1
        };
      } else {
        groupedStock[key].availableCount += 1;
      }
    });

    return Object.values(groupedStock);
  };

  const toggleStockSelection = (item) => {
    setStockSelectionData(prev => {
      const isSelected = prev.selectedStockItems.some(selected =>
        selected.id === item.id && selected.type === item.type
      );

      if (isSelected) {
        return {
          ...prev,
          selectedStockItems: prev.selectedStockItems.filter(selected =>
            !(selected.id === item.id && selected.type === item.type)
          )
        };
      } else {
        // เพิ่มข้อมูลสินค้าเมื่อเลือก
        const selectedItem = {
          ...item,
          quantity: 1
        };
        return {
          ...prev,
          selectedStockItems: [...prev.selectedStockItems, selectedItem]
        };
      }
    });
  };

  const confirmStockSelection = () => {
    const { productIndex, selectedStockItems } = stockSelectionData;

    if (productIndex !== null && selectedStockItems.length > 0) {
      const updatedProducts = [...quotationProducts];

      // ลบ row เดิม
      updatedProducts.splice(productIndex, 1);

      // สร้าง row ใหม่สำหรับแต่ละสินค้าที่เลือก
      const newProducts = selectedStockItems.map(item => ({
        type: item.type === 'battery' ? 'แบตเตอรี่' : 'เครื่องชาร์จ',
        brand: item.brand,
        capacity: item.capacity,
        description: `${item.type === 'battery' ? 'แบตเตอรี่' : 'เครื่องชาร์จ'} ${item.brand} ${item.capacity}`,
        quantity: 1,
        price: 0,
        stockItems: [item]
      }));

      // แทรก row ใหม่ในตำแหน่งเดิม
      updatedProducts.splice(productIndex, 0, ...newProducts);

      setQuotationProducts(updatedProducts);
    }

    closeStockSelectionModal();
  };

  const getUniqueBrands = () => {
    let allStock = [];

    if (stockSelectionData.filterType === 'all') {
      allStock = [
        ...batteryStock.filter(item => item.status === 'พร้อมขาย'),
        ...chargerStock.filter(item => item.status === 'พร้อมขาย')
      ];
    } else if (stockSelectionData.filterType === 'battery') {
      allStock = batteryStock.filter(item => item.status === 'พร้อมขาย');
    } else if (stockSelectionData.filterType === 'charger') {
      allStock = chargerStock.filter(item => item.status === 'พร้อมขาย');
    }

    return [...new Set(allStock.map(item => item.brand).filter(Boolean))];
  };

  const getUniqueCapacities = () => {
    let allStock = [];

    if (stockSelectionData.filterType === 'all') {
      allStock = [
        ...batteryStock.filter(item => item.status === 'พร้อมขาย'),
        ...chargerStock.filter(item => item.status === 'พร้อมขาย')
      ];
    } else if (stockSelectionData.filterType === 'battery') {
      allStock = batteryStock.filter(item => item.status === 'พร้อมขาย');
    } else if (stockSelectionData.filterType === 'charger') {
      allStock = chargerStock.filter(item => item.status === 'พร้อมขาย');
    }

    return [...new Set(allStock.map(item => item.capacity).filter(Boolean))];
  };

  const removeProduct = (index) => {
    setQuotationProducts(quotationProducts.filter((_, i) => i !== index));
  };

  const updateProduct = (index, field, value) => {
    const updatedProducts = [...quotationProducts];
    updatedProducts[index][field] = value;
    setQuotationProducts(updatedProducts);
  };

  const calculateTotal = () => {
    const subtotal = quotationProducts.reduce((sum, product) => sum + (product.price * product.quantity), 0);
    const discountAmount = Math.min(discount, subtotal); // ส่วนลดไม่เกินยอดรวม
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * 0.07; // ภาษี 7%
    const netTotal = afterDiscount + vatAmount;

    return {
      subtotal: subtotal,
      discountAmount: discountAmount,
      afterDiscount: afterDiscount,
      vatAmount: vatAmount,
      netTotal: netTotal
    };
  };

  // ฟังก์ชันแปลงตัวเลขเป็นข้อความภาษาไทย
  const numberToThaiText = (num) => {
    const ones = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
    const tens = ['', '', 'ยี่', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
    const places = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

    if (num === 0) return 'ศูนย์';

    let result = '';
    let numStr = num.toString();
    let len = numStr.length;

    for (let i = 0; i < len; i++) {
      let digit = parseInt(numStr[i]);
      let place = len - i - 1;

      if (digit !== 0) {
        if (place === 1 && digit === 1 && len > 1) {
          result += 'สิบ';
        } else if (place === 1 && digit === 2) {
          result += 'ยี่สิบ';
        } else {
          if (place === 0 && digit === 1 && len > 1 && parseInt(numStr[i - 1]) > 0) {
            result += 'เอ็ด';
          } else {
            result += ones[digit];
          }
          if (place > 0) {
            result += places[place];
          }
        }
      }
    }

    return result;
  };

  const bahtText = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    if (numericAmount === 0) return 'ศูนย์บาทถ้วน';

    const [baht, satang] = numericAmount.toFixed(2).split('.');
    let result = numberToThaiText(parseInt(baht)) + 'บาท';

    if (parseInt(satang) > 0) {
      result += numberToThaiText(parseInt(satang)) + 'สตางค์';
    } else {
      result += 'ถ้วน';
    }

    return result;
  };

  const handleSubmitQuotation = async () => {
    if (!selectedCustomer || quotationProducts.length === 0) {
      alert('กรุณาเลือกลูกค้าและเพิ่มสินค้าอย่างน้อย 1 รายการ');
      return;
    }

    try {
      // Generate quotation number first
      const numberResponse = await fetch('/api/quotations/generate-number');
      if (!numberResponse.ok) {
        throw new Error('ไม่สามารถสร้างเลขที่ใบเสนอราคาได้');
      }
      const numberData = await numberResponse.json();
      const quotationNumber = numberData.quotationNumber;

      const selectedCustomerData = customers.find(c => c.id === parseInt(selectedCustomer));
      const totals = calculateTotal();

      const quotationData = {
        quotationNumber: quotationNumber,
        customer_id: selectedCustomer,
        customer_name: selectedCustomerData?.customerName || '',
        customer_phone: selectedCustomerData?.phoneNumber || '',
        customer_address: selectedCustomerData?.mainAddress || '',
        customer_tax_id: selectedCustomerData?.taxId || '',
        customer_contact: selectedCustomerData?.contactPerson || '',
        customer_email: customerEmail,
        delivery_address: deliveryAddress,
        delivery_date: deliveryDate,
        sales_person: salesPerson,
        sales_phone: salesPhone,
        sales_email: salesEmail,
        payment_terms: paymentTerms,
        service_terms: serviceTerms,
        products: JSON.stringify(quotationProducts),
        subtotal: totals.subtotal,
        discount_amount: totals.discountAmount,
        vat_amount: totals.vatAmount,
        total_amount: totals.netTotal,
        notes: quotationNotes,
        status: 'pending',
        created_by: currentUser?.id || 1,
        userEmail: currentUser?.email || '',
        userPosition: currentUser?.position || '',
        discount: discount
      };

      await createQuotation(quotationData);
      alert('สร้างใบเสนอราคาเรียบร้อยแล้ว');
      closeQuotationForm();
    } catch (error) {
      console.error('Error creating quotation:', error);
      alert('เกิดข้อผิดพลาดในการสร้างใบเสนอราคา: ' + error.message);
    }
  };

  const previewQuotation = (quotation) => {
    try {
      // สร้างหน้าต่างใหม่สำหรับพรีวิว
      const previewWindow = window.open('', '_blank', 'width=800,height=600');

      if (!previewWindow) {
        alert('ไม่สามารถเปิดหน้าต่างพรีวิวได้ กรุณาอนุญาตให้เปิด popup');
        return;
      }

      const products = JSON.parse(quotation.products || '[]');

      const ITEMS_PER_FIRST_PAGE = 4;
      const ITEMS_PER_OTHER_PAGE = 12;

      const pages = [];
      const totalItems = products.length;

      // Calculate chunks
      let currentItemIndex = 0;
      let pageIndex = 1;

      while (currentItemIndex < totalItems) {
        const isFirstPage = pageIndex === 1;
        const itemsPerPage = isFirstPage ? ITEMS_PER_FIRST_PAGE : ITEMS_PER_OTHER_PAGE;
        const chunk = products.slice(currentItemIndex, currentItemIndex + itemsPerPage);

        // Check if this is the last page (to decide on totals)
        // Actually, we might need another page if the last chunk is full but we still need to show totals
        // But for simplicity, let's assume if we have items, we put them on a page. 
        // If it's the very last chunk of items, we append totals.
        // Wait, if the last chunk is full (e.g. 12 items), totals might not fit.
        // Let's use a safe limit. If it's the last batch of items, and we have > 8 items, maybe push totals to next page?
        // For now, let's stick to the simple logic: Last page of items gets the totals.

        pages.push({
          pageNumber: pageIndex,
          items: chunk,
          isFirstPage: isFirstPage,
          isLastPage: (currentItemIndex + chunk.length) >= totalItems,
          startIndex: currentItemIndex
        });

        currentItemIndex += chunk.length;
        pageIndex++;
      }

      // If no products, at least one page
      if (pages.length === 0) {
        pages.push({ pageNumber: 1, items: [], isFirstPage: true, isLastPage: true, startIndex: 0 });
      }

      const totalPages = pages.length;

      const renderHeader = () => `
        <div class="header">
          <div class="company-header">
            <div class="company-logo">
              <img src="/images/QuotationLogo.jpg" alt="Company Logo" style="width: 100px; height: auto; max-height: 80px; object-fit: contain;" />
            </div>
            <div class="company-details">
              <h3>บริษัท เค.ทีแพน จำกัด (สำนักงานใหญ่)</h3>
              <p>K.T.PAN CO., LTD.</p>
              <p>1408 9-10 ถนนพหลโยธิน แขวงจอมพล เขตจตุจักร กรุงเทพมหานคร 10900</p>
              <p>Ins: 02-939-3147</p>
              <p>เลขประจำตัวผู้เสียภาษี: 0105531018001</p>
            </div>
            <div class="quotation-title">
              <h1>ใบเสนอราคา</h1>
            </div>
          </div>
        </div>
      `;

      const renderCustomerInfo = () => `
        <div class="quotation-customer-info">
          <div class="customer-section">
            <p><strong>เลขประจำตัวผู้เสียภาษี:</strong> ${quotation.customer_tax_id || '-'}</p>
            <p><strong>ลูกค้า:</strong> ${quotation.customer_name}</p>
            <p><strong>ที่อยู่:</strong> ${quotation.customer_address || 'ไม่ระบุ'}</p>
            <p><strong>ผู้ติดต่อ:</strong> ${quotation.customer_contact || 'คุณ ' + quotation.customer_name}</p>
            <p><strong>เบอร์ติดต่อ:</strong> ${quotation.customer_phone}</p>
            <p><strong>อีเมล:</strong> ${quotation.customer_email || '-'}</p>
            <p><strong>ที่อยู่จัดส่ง:</strong> ${quotation.delivery_address || quotation.customer_address || 'ตามที่อยู่หลัก'}</p>
          </div>
          <div class="quotation-details">
            <p><strong>เลขที่:</strong> ${quotation.id === 'PREVIEW' ? 'PREVIEW-001' : (quotation.quotationNumber || `Q${String(quotation.id).padStart(4, '0')}`)}</p>
            <p><strong>วันที่:</strong> ${new Date().toLocaleDateString('th-TH')}</p>
            <p><strong>กำหนดยืนราคา:</strong> ${quotation.delivery_date || '30 วัน'}</p>
            <p><strong>ผู้ขาย:</strong> ${quotation.sales_person || 'คุณธวัช ตั้งไพบูลย์ทรัพย์'}</p>
            <p><strong>เบอร์โทรผู้ขาย:</strong> ${quotation.sales_phone || '089-494-1973'}</p>
            <p><strong>อีเมลผู้ขาย:</strong> ${quotation.sales_email || 'thawat.t@ktpan.com'}</p>
          </div>
        </div>
      `;

      const renderTable = (items, startIndex) => `
        <div class="product-title">
          <h3>รายละเอียดสินค้าหรือบริการ</h3>
        </div>
        <table class="product-table">
          <thead>
            <tr>
              <th width="8%">ลำดับ</th>
              <th width="35%">รายการ</th>
              <th width="10%">จำนวน</th>
              <th width="10%">หน่วย</th>
              <th width="15%">ราคาต่อหน่วย</th>
              <th width="22%">รวม</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((product, index) => `
              <tr>
                <td class="text-center">${startIndex + index + 1}</td>
                <td class="text-left">
                  ${product.stockItems && product.stockItems.length > 0 ?
          `${product.stockItems[0].type === 'battery' ? 'แบตเตอรี่' : product.stockItems[0].type === 'charger' ? 'เครื่องชาร์จ' : 'บริการ'}<br>
                     <small>${product.stockItems[0].brand || 'wiretech'} ${product.stockItems[0].capacity || '105'} ${product.stockItems[0].type === 'battery' ? 'Ah.' : 'A.'}</small>` :
          (product.description || 'บริการติดตั้งระบบสำรองและตรวจเช็ค')
        }
                </td>
                <td class="text-center">${product.quantity}</td>
                <td class="text-center">Ea.</td>
                <td class="text-right">${(parseFloat(product.price) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                <td class="text-right">${((parseFloat(product.price) || 0) * (parseFloat(product.quantity) || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;

      const renderTotals = () => `
        <div class="total-section">
          <table class="left-baht-table">
            <tr>
              <td class="baht-text">${(() => {
          const amount = quotation.total_amount || 0;
          if (amount === 0) return 'ศูนย์บาทถ้วน';
          const numberToThaiText = (num) => {
            const ones = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
            const tens = ['', '', 'ยี่', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
            const places = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];
            if (num === 0) return 'ศูนย์';
            let result = '';
            let numStr = num.toString();
            let len = numStr.length;
            for (let i = 0; i < len; i++) {
              let digit = parseInt(numStr[i]);
              let place = len - i - 1;
              if (digit !== 0) {
                if (place === 1 && digit === 1) {
                  result += '';
                } else if (place === 1 && digit === 2) {
                  result += 'ยี่';
                } else if (place === 0 && digit === 1 && len > 1) {
                  result += 'เอ็ด';
                } else {
                  result += ones[digit];
                }

                if (place === 1 && digit === 1) {
                  result += 'สิบ';
                } else if (place !== 0 || digit !== 0) {
                  result += places[place];
                }
              } else if (place === 1 && digit === 0 && numStr[i - 1] !== '0') {
                // Handle cases like 101, 201 etc where tens digit is 0
              }
            }
            return result;
          };

          const baht = Math.floor(amount);
          const satang = Math.round((amount - baht) * 100);
          let text = numberToThaiText(baht) + 'บาท';
          if (satang > 0) {
            text += numberToThaiText(satang) + 'สตางค์';
          } else {
            text += 'ถ้วน';
          }
          return text;
        })()}</td>
            </tr>
          </table>
          
          <table class="total-table">
            <tr>
              <td class="total-label">รวมราคาสินค้า</td>
              <td class="total-amount">${Number(quotation.subtotal || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
            </tr>
            <tr>
              <td class="total-label">ส่วนลด</td>
              <td class="total-amount">${Number(quotation.discount_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
            </tr>
            <tr>
              <td class="total-label">ภาษีมูลค่าเพิ่ม 7%</td>
              <td class="total-amount">${Number(quotation.vat_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
            </tr>
            <tr class="grand-total">
              <td class="total-label"><strong>ราคาสุทธิ</strong></td>
              <td class="total-amount grand-total-amount"><strong>${Number(quotation.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong></td>
            </tr>
          </table>
        </div>
        
        <div class="payment-terms">
          <p><strong>เงื่อนไขการชำระ:</strong> ${quotation.payment_terms || 'มัดจำ 50 % วันส่งมือ ชำระส่วนที่เหลือส่งงาน'}</p>
          <p><strong>เงื่อนไขการบริการสินค้า:</strong> ${quotation.service_terms || 'รับประกัน แบตเตอรี่ 36 เดือน รับประกัน ชาร์จเจอร์ 12 เดือน รับประกัน งานติดตั้ง 7 วัน'}</p>
          ${quotation.notes ? `<p><strong>หมายเหตุ:</strong> ${quotation.notes}</p>` : ''}
        </div>
      `;

      const renderSignature = () => `
        <div class="signature-section">
          <div class="signature-left">
            <div class="signature-line"></div>
            <div class="sig-name">คุณ ${quotation.sales_person || 'คุณรอง ดิ่งไพบูลย์ธนรัพย์'}</div>
            <div class="sig-role">ผู้เสนอราคา</div>
          </div>
          <div class="signature-right">
            <div class="signature-line"></div>
            <div class="sig-name">( อนุมัติสั่งซื้อ )</div>
          </div>
        </div>
      `;

      const previewContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${quotation.id === 'PREVIEW' ? 'พรีวิวใบเสนอราคา' : `ใบเสนอราคา Q${String(quotation.id).padStart(4, '0')}`}</title>
        <meta charset="utf-8">
        <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
        <style>
          body { font-family: 'Sarabun', Arial, sans-serif; margin: 0; font-size: 14px; background: #f5f5f5; }
          .header { margin-bottom: 10px; }
          .company-header { display: flex; justify-content: space-between; align-items: flex-start; border: 1px solid #0066cc; padding: 15px; background: white; border-radius: 8px; }
          .company-logo { flex: 0 0 100px; margin-right: 20px; }
          .company-details { flex: 1; font-size: 12px; color: #000; }
          .company-details h3 { margin: 0 0 5px 0; font-size: 16px; font-weight: bold; }
          .company-details p { margin: 2px 0; }
          .quotation-title { flex: 0 0 150px; text-align: right; }
          .quotation-title h1 { background: #0066cc; color: white; padding: 10px 20px; margin: 0; font-size: 18px; display: inline-block; border-radius: 6px; }
          
          .quotation-customer-info { display: flex; margin: 10px 0; border: 1px solid #ccc; border-radius: 8px; overflow: hidden; }
          .customer-section { flex: 6; padding: 15px; border-right: 1px solid #ccc; }
          .quotation-details { flex: 4; padding: 15px; }
          .customer-section p, .quotation-details p { margin: 5px 0; font-size: 12px; line-height: 1.5; }
          
          .product-title { margin: 10px 0 0 0; text-align: center; }
          .product-title h3 { background: #ffffffff; color: #0066cc; padding: 12px; margin: 0; font-size: 16px; border-radius: 6px; }
          
          .product-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .product-table th, .product-table td { border: 1px solid #eaeaeaff; padding: 12px 8px; font-size: 12px; vertical-align: middle; border-radius: 1px; }
          .product-table th { background-color: #0066cc; color: white; text-align: center; font-weight: bold; border-bottom: none; }
          
          /* Fix specificity issues by targeting the cell directly with the class */
          .product-table td.text-right { text-align: right; }
          .product-table td.text-center { text-align: center; }
          .product-table td.text-left { text-align: left; }
          
          .total-section { margin: 10px 0; display: grid; grid-template-columns: 1fr 360px; column-gap: 16px; align-items: start; }
          .baht-box { width: 100%; border: 1px solid #0066cc; padding: 10px; text-align: center; font-weight: bold; color: #000; background: #fff; font-size: 14px; margin-top: 10px; border-radius: 6px; }
          
          .total-table { width: 100%; border-collapse: collapse; }
          .total-table td { border: 1px solid #eaeaeaff; padding: 8px; font-size: 12px; }
          .total-label { background: #ffffff; color: #000; font-weight: bold; text-align: right; }
          .total-amount { text-align: right; background: #ffffff; }
          
          .grand-total td { background: #004499 !important; color: #fff !important; font-weight: bold; border-color: #004499; }
          .grand-total-amount { font-size: 16px; }
          
          .left-baht-table { width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 10px; }
          .left-baht-table td { border: 1px solid #0066cc; padding: 15px; text-align: center; border-radius: 6px; }
          .left-baht-table .baht-text { font-weight: bold; color: #000; font-size: 14px; }
          
          .page { 
            background: white;
            width: 210mm;
            min-height: 297mm;
            padding: 10mm;
            margin: 10mm auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            display: flex; 
            flex-direction: column; 
            box-sizing: border-box;
            position: relative;
          }

          .page-number {
            position: absolute;
            bottom: 10mm;
            right: 10mm;
            font-size: 10px;
            color: #666;
          }
          
          html, body { height: 100%; }
          .content-wrapper { flex: 1; display: flex; flex-direction: column; padding-bottom: 40px; }
          .signature-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            column-gap: 64px;
            justify-items: center;
            align-items: end;
            margin-bottom: 0;
            padding-top: 10px;
            min-height: 120px;
          }
          .signature-left, .signature-right { text-align: center; width: 100%; }
          .signature-line {
            width: 80%;
            height: 1px;
            background: #000;
            margin: 20px auto 10px auto;
          }
          .sig-name { font-weight: bold; font-size: 14px; margin-bottom: 5px; }
          .sig-role { font-size: 12px; color: #444; }
          
          .payment-terms { margin: 20px 0 10px 0; font-size: 12px; margin-top: auto; }
          .payment-terms p { margin: 5px 0; }
          
          .action-buttons {
            position: fixed;
            bottom: 30px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 12px;
            background: white;
            padding: 10px 20px;
            border-radius: 50px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.15);
            z-index: 2000;
            border: 1px solid rgba(0,0,0,0.05);
          }
          
          .btn-action {
            padding: 8px 20px;
            border: none;
            border-radius: 25px;
            cursor: pointer;
            font-family: 'Sarabun', sans-serif;
            font-size: 14px;
            font-weight: 600;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          
          .btn-print {
            background: #fff;
            color: #333;
            border: 1px solid #e0e0e0;
          }
          .btn-print:hover { background: #f5f5f5; }
          
          .btn-download {
            background: linear-gradient(135deg, #0066cc 0%, #0052a3 100%);
            color: white;
            box-shadow: 0 2px 8px rgba(0, 102, 204, 0.3);
          }
          .btn-download:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 102, 204, 0.4);
          }
          
          .btn-close {
            background: #fff;
            color: #dc3545;
            border: 1px solid #ffd7d7;
          }
          .btn-close:hover { background: #fff5f5; }

          @media print { 
            @page { 
              size: A4; 
              margin: 0; 
            }
            body { 
              background: white; 
              margin: 0; 
              padding: 0;
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
            }
            .page { 
              width: 210mm; 
              height: 296mm; 
              margin: 0; 
              padding: 10mm; 
              box-shadow: none; 
              border: none;
              page-break-after: always; 
              overflow: hidden;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
            }
            .page:last-child { 
              page-break-after: auto; 
            }
            .header { page-break-inside: avoid; }
            .content-wrapper { 
              flex: 1; 
              display: flex; 
              flex-direction: column; 
              padding-bottom: 0; 
            } 
            .signature-section { 
              margin-top: 40px; 
              page-break-inside: avoid; 
            }
            .action-buttons { 
              display: none !important; 
            }
            
            /* Hide URL/Page info printed by browser if possible (though @page margin 0 usually handles this) */
          }
        </style>
      </head>
      <body>
        <div id="quotation-content">
          ${pages.map(page => `
            <div class="page">
              <div class="content-wrapper">
                ${renderHeader()}
                ${page.isFirstPage ? renderCustomerInfo() : ''}
                ${renderTable(page.items, page.startIndex)}
                ${page.isLastPage ? renderTotals() : ''}
                ${page.isLastPage ? renderSignature() : ''}
              </div>
              <div class="page-number">หน้า ${page.pageNumber} / ${totalPages}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="action-buttons" data-html2canvas-ignore="true">
          <button onclick="window.print()" class="btn-action btn-print">
            <svg style="width:16px;height:16px" viewBox="0 0 24 24"><path fill="currentColor" d="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z" /></svg>
            พิมพ์
          </button>
          <button onclick="downloadPDF()" class="btn-action btn-download">
            <svg style="width:16px;height:16px" viewBox="0 0 24 24"><path fill="currentColor" d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" /></svg>
            ดาวน์โหลด PDF
          </button>
          <button onclick="window.close()" class="btn-action btn-close">
            <svg style="width:16px;height:16px" viewBox="0 0 24 24"><path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" /></svg>
            ปิด
          </button>
        </div>
        
        <script>
          function downloadPDF() {
            console.log('Starting downloadPDF...');
            try {
              if (typeof html2pdf === 'undefined') {
                alert('Error: html2pdf library not loaded. Please check your internet connection or try again in a few seconds.');
                return;
              }

              // 1. Create Loading Overlay
              const overlay = document.createElement('div');
              overlay.style.position = 'fixed';
              overlay.style.top = '0';
              overlay.style.left = '0';
              overlay.style.width = '100%';
              overlay.style.height = '100%';
              overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              overlay.style.zIndex = '9999';
              overlay.style.display = 'flex';
              overlay.style.justifyContent = 'center';
              overlay.style.alignItems = 'center';
              overlay.style.flexDirection = 'column';
              overlay.innerHTML = '<div style="font-family: Sarabun, sans-serif; font-size: 24px; color: #333;">กำลังสร้างไฟล์ PDF...</div><div style="margin-top: 10px; font-family: Sarabun, sans-serif; color: #666;">กรุณารอสักครู่</div>';
              document.body.appendChild(overlay);

              const element = document.getElementById('quotation-content');
              
              const opt = {
                margin: [0, 0, 0, 0],
                filename: 'quotation_${quotation.quotationNumber || 'preview'}.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { 
                  scale: 2, 
                  useCORS: true,
                  logging: true,
                  scrollY: 0,
                  onclone: (clonedDoc) => {
                    console.log('onclone called');
                    
                    // Global reset for the cloned document to prevent any ghost margins
                    const body = clonedDoc.body;
                    const html = clonedDoc.documentElement;
                    
                    body.style.margin = '0';
                    body.style.padding = '0';
                    body.style.overflow = 'hidden';
                    html.style.margin = '0';
                    html.style.padding = '0';
                    html.style.overflow = 'hidden';

                    const pages = clonedDoc.querySelectorAll('.page');
                    const totalPages = pages.length;

                    // Force the container to match A4 width and calculated exact height
                    const container = clonedDoc.getElementById('quotation-content');
                    if (container) {
                      container.style.width = '210mm';
                      // Use 296.5mm per page to be extremely safe against rounding errors
                      container.style.height = (totalPages * 296.5) + 'mm'; 
                      container.style.margin = '0';
                      container.style.padding = '0';
                      container.style.backgroundColor = 'white';
                      container.style.overflow = 'hidden';
                      container.style.boxSizing = 'border-box';
                      container.style.display = 'block'; // Ensure block layout
                    }

                    pages.forEach((page, index) => {
                      // 1. Basic settings
                      page.style.width = '210mm';
                      page.style.height = '296.5mm'; // Match container per-page height
                      page.style.maxHeight = '296.5mm';
                      page.style.overflow = 'hidden'; 
                      page.style.padding = '10mm';
                      page.style.margin = '0';
                      page.style.marginBottom = '0';
                      page.style.border = 'none';
                      page.style.background = 'white';
                      page.style.display = 'flex';
                      page.style.flexDirection = 'column';
                      page.style.boxSizing = 'border-box'; // Critical
                      
                      // 2. Page break logic
                      if (index > 0) {
                        page.style.pageBreakBefore = 'always';
                      } else {
                        page.style.pageBreakBefore = 'auto';
                      }
                      page.style.pageBreakAfter = 'avoid'; 
                    });
                    
                    const buttons = clonedDoc.querySelector('.action-buttons');
                    if (buttons) {
                        buttons.parentElement.removeChild(buttons); // Remove from DOM completely
                    }
                  }
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: 'css' }
              };
              
              // 4. Generate PDF
              html2pdf().set(opt).from(element).save().then(() => {
                console.log('PDF generated successfully');
                document.body.removeChild(overlay);
              }).catch(err => {
                console.error('PDF Generation Error (Promise):', err);
                alert('เกิดข้อผิดพลาดในการสร้าง PDF: ' + err.message);
                if (document.body.contains(overlay)) {
                  document.body.removeChild(overlay);
                }
              });
            } catch (error) {
              console.error('PDF Generation Error (Sync):', error);
              alert('เกิดข้อผิดพลาด: ' + error.message);
              // Try to remove overlay if it exists
              const overlay = document.querySelector('div[style*="z-index: 9999"]');
              if (overlay) document.body.removeChild(overlay);
            }
          }
        </script>
      </body>
      </html>
    `;

      previewWindow.document.write(previewContent);
      previewWindow.document.close();
    } catch (error) {
      console.error('Error in previewQuotation:', error);
      alert('เกิดข้อผิดพลาดในการแสดงพรีวิว: ' + error.message);
    }
  };

  const downloadQuotationPDF = (quotation) => {
    // เรียกใช้ฟังก์ชัน preview และให้ผู้ใช้พิมพ์เป็น PDF
    previewQuotation(quotation);
  };

  const previewQuotationForm = () => {
    if (!selectedCustomer || quotationProducts.length === 0) {
      alert('กรุณาเลือกลูกค้าและเพิ่มสินค้าอย่างน้อย 1 รายการ');
      return;
    }

    const selectedCustomerData = customers.find(c => c.id === parseInt(selectedCustomer));
    const totals = calculateTotal();

    // สร้างข้อมูลใบเสนอราคาชั่วคราวสำหรับพรีวิว
    const tempQuotationData = {
      id: 'PREVIEW',
      customer_name: selectedCustomerData?.customerName || '',
      customer_phone: selectedCustomerData?.phoneNumber || '',
      customer_address: selectedCustomerData?.mainAddress || '',
      customer_tax_id: selectedCustomerData?.taxId || '',
      customer_contact: selectedCustomerData?.contactPerson || '',
      customer_email: customerEmail,
      delivery_address: deliveryAddress,
      delivery_date: deliveryDate,
      sales_person: salesPerson,
      sales_phone: salesPhone,
      sales_email: salesEmail,
      payment_terms: paymentTerms,
      service_terms: serviceTerms,
      products: JSON.stringify(quotationProducts),
      subtotal: totals.subtotal,
      discount_amount: totals.discountAmount,
      vat_amount: totals.vatAmount,
      total_amount: totals.netTotal,
      notes: quotationNotes,
      created_at: new Date().toISOString(),
      status: 'preview'
    };

    // Debug logging
    console.log('Preview Data:', {
      subtotal: tempQuotationData.subtotal,
      discount_amount: tempQuotationData.discount_amount,
      vat_amount: tempQuotationData.vat_amount,
      total_amount: tempQuotationData.total_amount
    });

    // เรียกใช้ฟังก์ชัน previewQuotation ที่มีอยู่แล้ว
    previewQuotation(tempQuotationData);
  };

  const openSalePopup = (quotation) => {
    setSelectedQuotation(quotation);
    setShowSalePopup(true);
    // เตรียมข้อมูลสำหรับการเลือกซีเรียลนัมเบอร์
    const products = JSON.parse(quotation.products || '[]');
    const initialSerials = {};
    const initialWarrantyPeriods = {};
    const initialPmServiceCounts = {};
    products.forEach((product, index) => {
      initialSerials[index] = [];
      // ตั้งค่าเริ่มต้นสำหรับระยะเวลารับประกัน (เฉพาะแบตเตอรี่และชาร์จเจอร์)
      if (product.type === 'แบตเตอรี่' || product.type === 'เครื่องชาร์จ') {
        initialWarrantyPeriods[index] = product.type === 'แบตเตอรี่' ? '36' : '12'; // 36 เดือน สำหรับแบตเตอรี่, 12 เดือน สำหรับชาร์จเจอร์
        initialPmServiceCounts[index] = ''; // ค่าเริ่มต้นสำหรับจำนวนครั้ง PM service
      }
    });
    setSelectedSerials(initialSerials);
    setWarrantyPeriods(initialWarrantyPeriods);
    setWarrantyErrors({});
    setPmServiceCounts(initialPmServiceCounts);
    setPmServiceErrors({});
    // ตั้งค่าเริ่มต้นวันส่งมอบเป็นวันนี้
    try {
      const today = new Date().toISOString().split('T')[0];
      setSaleDeliveryDate(today);
    } catch (e) { }
  };

  const closeSalePopup = () => {
    setShowSalePopup(false);
    setSelectedQuotation(null);
    setSelectedSerials({});
    setWarrantyPeriods({});
    setWarrantyErrors({});
    setPmServiceCounts({});
    setPmServiceErrors({});
    setSaleDeliveryDate('');
  };

  // ฟังก์ชันสำหรับจัดการการเปลี่ยนแปลงระยะเวลารับประกัน
  const handleWarrantyPeriodChange = (productIndex, value) => {
    const newWarrantyPeriods = { ...warrantyPeriods };
    const newWarrantyErrors = { ...warrantyErrors };

    // ตรวจสอบว่าเป็นตัวเลขเท่านั้น
    if (value === '' || /^\d+$/.test(value)) {
      newWarrantyPeriods[productIndex] = value;
      delete newWarrantyErrors[productIndex];
    } else {
      newWarrantyErrors[productIndex] = 'กรุณากรอกตัวเลขเท่านั้น';
    }

    setWarrantyPeriods(newWarrantyPeriods);
    setWarrantyErrors(newWarrantyErrors);
  };

  // ฟังก์ชันสำหรับจัดการการเปลี่ยนแปลงจำนวนครั้ง PM service
  const handlePmServiceCountChange = (productIndex, value) => {
    const newPmServiceCounts = { ...pmServiceCounts };
    const newPmServiceErrors = { ...pmServiceErrors };

    // ตรวจสอบว่าเป็นตัวเลขเท่านั้น
    if (value === '' || /^\d+$/.test(value)) {
      newPmServiceCounts[productIndex] = value;
      delete newPmServiceErrors[productIndex];
    } else {
      newPmServiceErrors[productIndex] = 'กรุณากรอกตัวเลขเท่านั้น';
    }

    setPmServiceCounts(newPmServiceCounts);
    setPmServiceErrors(newPmServiceErrors);
  };

  // ฟังก์ชันคำนวณวันหมดประกัน
  const calculateWarrantyEndDate = (warrantyMonths, saleDate = null) => {
    if (!warrantyMonths || warrantyMonths === '') return '';
    try {
      const startDate = saleDate ? new Date(saleDate) : new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + parseInt(warrantyMonths));

      return endDate.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error calculating warranty end date:', error);
      return '-';
    }
  };

  const handleConfirmSale = async () => {
    if (!selectedQuotation) return;

    if (isProcessingSale) {
      alert('กำลังดำเนินการปิดการขาย กรุณารอสักครู่...');
      return;
    }

    // ตรวจสอบว่าเลือกซีเรียลนัมเบอร์ครบทุกรายการแล้ว (เฉพาะสินค้าที่ไม่ใช่บริการทั่วไป)
    const products = JSON.parse(selectedQuotation.products || '[]');
    const allSerialsSelected = products.every((product, index) => {
      // ตรวจสอบว่าเป็นบริการทั่วไปหรือไม่
      const isGeneralService = !product.type ||
        product.type === 'บริการ' ||
        product.type === 'การบริการ' ||
        product.type === 'service' ||
        product.type === 'อะไหล่' ||
        (product.description && product.description.includes('บริการ'));

      // ถ้าเป็นบริการทั่วไป ไม่ต้องเลือกซีเรียล
      if (isGeneralService) {
        return true;
      }

      // สำหรับสินค้าปกติ ต้องเลือกซีเรียลครบ
      const requiredCount = product.quantity;
      const selectedCount = selectedSerials[index]?.filter(serial => serial).length || 0;
      return selectedCount === requiredCount;
    });

    if (!allSerialsSelected) {
      alert('กรุณาเลือกซีเรียลนัมเบอร์ให้ครบทุกรายการสินค้า (ยกเว้นบริการทั่วไป)');
      return;
    }

    // ตรวจสอบข้อมูลระยะเวลารับประกันสำหรับแบตเตอรี่และชาร์จเจอร์
    const warrantyValidationErrors = [];
    products.forEach((product, index) => {
      if ((product.type === 'แบตเตอรี่' || product.type === 'เครื่องชาร์จ') &&
        (!warrantyPeriods[index] || warrantyPeriods[index] === '')) {
        warrantyValidationErrors.push(`กรุณากรอกระยะเวลารับประกันสำหรับ ${product.type}`);
      }
    });

    if (warrantyValidationErrors.length > 0) {
      alert(warrantyValidationErrors.join('\n'));
      return;
    }

    // ตรวจสอบข้อผิดพลาดในการกรอกข้อมูลระยะเวลารับประกัน
    if (Object.keys(warrantyErrors).length > 0) {
      alert('กรุณาแก้ไขข้อผิดพลาดในการกรอกระยะเวลารับประกัน');
      return;
    }

    // ตรวจสอบข้อผิดพลาดในการกรอกข้อมูล PM service
    if (Object.keys(pmServiceErrors).length > 0) {
      alert('กรุณาแก้ไขข้อผิดพลาดในการกรอกจำนวนครั้ง PM service');
      return;
    }

    // เตรียมข้อมูลสินค้าพร้อมซีเรียลที่เลือก
    const saleDate = new Date().toISOString();
    const productsWithSelectedSerials = products.map((product, index) => {
      const productCopy = { ...product };

      // ตรวจสอบว่าเป็นบริการทั่วไปหรือไม่
      const isGeneralService = !product.stockItems || product.stockItems.length === 0 || product.type === 'การบริการ';

      if (!isGeneralService && selectedSerials[index]) {
        // อัพเดท stockItems ด้วยซีเรียลที่เลือก
        productCopy.stockItems = selectedSerials[index].filter(serial => serial).map(serial => {
          // หา stockItem ที่ตรงกับซีเรียลที่เลือก
          const originalStockItem = [...batteryStock, ...chargerStock].find(item => item.serial === serial);
          return {
            ...originalStockItem,
            serial: serial,
            quantity: 1 // แต่ละซีเรียลมีจำนวน 1 ชิ้น
          };
        });

        // อัพเดทจำนวนสินค้าตามซีเรียลที่เลือก
        productCopy.quantity = productCopy.stockItems.length;
      }

      return productCopy;
    });

    // รวบรวมซีเรียลนัมเบอร์ทั้งหมด (เฉพาะสินค้าที่ไม่ใช่บริการ)
    const allSelectedSerials = Object.values(selectedSerials).flat().filter(serial => serial);

    // เตรียมข้อมูลระยะเวลารับประกันสำหรับแต่ละสินค้า
    const warrantyData = {};
    products.forEach((product, index) => {
      if (product.type === 'แบตเตอรี่' || product.type === 'เครื่องชาร์จ') {
        warrantyData[index] = {
          warrantyMonths: parseInt(warrantyPeriods[index] || 0),
          warrantyEndDate: calculateWarrantyEndDate(warrantyPeriods[index], saleDate)
        };
      }
    });

    // เตรียมข้อมูล PM service สำหรับแต่ละสินค้า
    const pmServiceData = {};
    products.forEach((product, index) => {
      if (product.type === 'แบตเตอรี่' || product.type === 'เครื่องชาร์จ') {
        pmServiceData[index] = {
          pmServiceCount: parseInt(pmServiceCounts[index] || 0)
        };
      }
    });

    setIsProcessingSale(true);
    try {
      const saleData = {
        customer_id: selectedQuotation.customer_id,
        customer_name: selectedQuotation.customer_name,
        customer_phone: selectedQuotation.customer_phone,
        customer_address: selectedQuotation.customer_address,
        products: JSON.stringify(productsWithSelectedSerials), // ใช้ข้อมูลสินค้าที่มีซีเรียลที่เลือกแล้ว
        total_amount: selectedQuotation.total_amount,
        notes: selectedQuotation.notes,
        quotationId: selectedQuotation.id, // เปลี่ยนจาก quotation_id เป็น quotationId
        created_by: currentUser?.id || 1,
        userEmail: currentUser?.email || '',
        userPosition: currentUser?.position || '',
        discount: selectedQuotation.discount || 0,
        serialNumber: allSelectedSerials.join(','),
        warrantyData: JSON.stringify(warrantyData),
        pmServiceData: JSON.stringify(pmServiceData),
        salesperson: salesPerson || currentUser?.name || currentUser?.username || '',
        saleDate: saleDate.split('T')[0], // เพิ่ม saleDate สำหรับการคำนวณวันหมดอายุการรับประกัน
        // เพิ่มวันที่ส่งมอบสำหรับการบันทึกและคำนวณ PM schedule
        delivery_date: saleDeliveryDate || undefined
      };

      console.log('Creating sale with data:', saleData);
      const result = await createSale(saleData);

      if (!result.success) {
        throw new Error(result.error || 'ไม่สามารถบันทึกการขายได้');
      }

      console.log('Sale created successfully:', result.data);

      // รีเฟรชข้อมูลใบเสนอราคาและสต็อกหลังจากปิดการขาย
      await fetchQuotations();
      await fetchBatteryStock();
      await fetchChargerStock();

      // ส่ง event ไปยัง SalePage เพื่ออัพเดทข้อมูล
      const event = new CustomEvent('quotationSaleCreated', {
        detail: {
          quotationData: selectedQuotation,
          selectedSerials: allSelectedSerials,
          selectedSerialsDetail: selectedSerials, // เพิ่มรายละเอียดซีเรียลที่เลือกแยกตามสินค้า
          saleData: saleData
        }
      });
      window.dispatchEvent(event);

      // เตรียมข้อมูลสำหรับหน้ายืนยันการปิดการขาย
      setSaleConfirmationData({
        quotation: selectedQuotation,
        selectedSerials: selectedSerials,
        warrantyPeriods: warrantyPeriods,
        saleData: saleData,
        saleResult: result.data
      });

      // ปิด popup การขายและแสดงหน้ายืนยัน
      closeSalePopup();
      setShowSaleConfirmation(true);
    } catch (error) {
      console.error('Error creating sale:', error);
      alert(`เกิดข้อผิดพลาดในการปิดการขาย: ${error.message}`);
    } finally {
      setIsProcessingSale(false);
    }
  };

  // Function to edit quotation
  const editQuotation = (quotation) => {
    try {
      // Parse products from JSON string
      const products = JSON.parse(quotation.products || '[]');

      // Set edit mode and store quotation data
      setIsEditMode(true);
      setEditingQuotation(quotation);

      // Set form data with quotation values
      setSelectedCustomer(quotation.customer_id?.toString() || '');
      setQuotationProducts(products);
      setQuotationNotes(quotation.notes || '');
      setDiscount(quotation.discount || 0);
      setCustomerEmail(quotation.customer_email || '');
      setDeliveryAddress(quotation.delivery_address || '');
      setSalesPerson(quotation.sales_person || '');
      setSalesPhone(quotation.sales_phone || '');
      setSalesEmail(quotation.sales_email || '');
      setDeliveryDate(quotation.delivery_date || '30 วัน');
      setPaymentTerms(quotation.payment_terms || 'มัดจำ 50 % วันส่งมือ ชำระส่วนที่เหลือส่งงาน');
      setServiceTerms(quotation.service_terms || 'รับประกัน แบตเตอรี่ 36 เดือน รับประกัน ชาร์จเจอร์ 12 เดือน รับประกัน งานติดตั้ง 7 วัน');

      // Open form for editing
      setShowQuotationForm(true);
    } catch (error) {
      console.error('Error editing quotation:', error);
      alert('เกิดข้อผิดพลาดในการแก้ไขใบเสนอราคา');
    }
  };

  // Function to delete quotation
  const handleDeleteQuotation = async (quotation) => {
    if (window.confirm(`คุณต้องการลบใบเสนอราคาเลขที่ ${quotation.quotationNumber || 'Q' + String(quotation.id).padStart(4, '0')} หรือไม่?`)) {
      try {
        const result = await deleteQuotation(quotation.id);
        if (result.success) {
          alert('ลบใบเสนอราคาเรียบร้อยแล้ว');
          // Refresh quotations list using hook
          await fetchQuotations();
        } else {
          throw new Error(result.error || 'Failed to delete quotation');
        }
      } catch (error) {
        console.error('Error deleting quotation:', error);
        alert('เกิดข้อผิดพลาดในการลบใบเสนอราคา: ' + error.message);
      }
    }
  };

  const customModalStyles = {
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '2rem',
      zIndex: 1050,
      overflowY: 'auto'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '95%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    modalBody: {
      padding: '1.5rem',
      maxHeight: '70vh',
      overflowY: 'auto'
    }
  };

  if (customersLoading || quotationsLoading || usersLoading || salesLoading || batteryLoading || chargerLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0"><i className="fas fa-file-invoice-dollar me-2"></i>ใบเสนอราคา</h2>
            <button
              type="button"
              className="btn btn-primary"
              onClick={openQuotationForm}
            >
              <i className="fas fa-plus-circle me-2"></i>สร้างใบเสนอราคา
            </button>
          </div>

          {/* Custom modal for quotation form */}
          {showQuotationForm && (
            <div style={customModalStyles.modal}>
              <div style={customModalStyles.modalContent}>
                <div className="modal-header border-bottom p-3">
                  <h5 className="modal-title mb-0">
                    <i className="fas fa-file-invoice-dollar me-2"></i>
                    {isEditMode ?
                      `แก้ไขใบเสนอราคา ${editingQuotation?.quotationNumber || 'Q' + String(editingQuotation?.id).padStart(4, '0')}` :
                      'สร้างใบเสนอราคา'
                    }
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeQuotationForm}
                  ></button>
                </div>

                <div style={customModalStyles.modalBody}>
                  {/* Customer Selection */}
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0"><i className="fas fa-user me-2"></i>ข้อมูลลูกค้า</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label">เลือกลูกค้า *</label>
                          <select
                            className="form-select"
                            value={selectedCustomer}
                            onChange={(e) => setSelectedCustomer(e.target.value)}
                            required
                            disabled={customersLoading}
                          >
                            <option value="">
                              {customersLoading ? "กำลังโหลดข้อมูลลูกค้า..." : "-- เลือกลูกค้า --"}
                            </option>
                            {customers && customers.length > 0 ? customers.map(customer => (
                              <option key={customer.id} value={customer.id}>
                                {customer.customerName} - {customer.phoneNumber}
                              </option>
                            )) : (
                              !customersLoading && <option disabled>ไม่มีข้อมูลลูกค้า</option>
                            )}
                          </select>
                        </div>
                      </div>

                      {/* Display selected customer details */}
                      {selectedCustomer && (() => {
                        const customer = customers.find(c => c.id === parseInt(selectedCustomer));
                        return customer ? (
                          <div className="mt-3 p-3 bg-light rounded">
                            <div className="row">
                              <div className="col-md-6">
                                <strong>ชื่อ:</strong> {customer.customerName}<br />
                                <strong>เบอร์โทร:</strong> {customer.phoneNumber}<br />
                                <strong>ผู้ติดต่อ:</strong> {customer.contactPerson || 'ไม่ระบุ'}
                              </div>
                              <div className="col-md-6">
                                <strong>ที่อยู่:</strong> {customer.mainAddress}<br />
                                <strong>เลขประจำตัวผู้เสียภาษี:</strong> {customer.taxId || 'ไม่ระบุ'}
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Additional Customer Information */}
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <label className="form-label">อีเมลลูกค้า</label>
                          <input
                            type="email"
                            className="form-control"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            placeholder="customer@example.com"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">กำหนดยืนราคา</label>
                          <input
                            type="text"
                            className="form-control"
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            placeholder="30 วัน"
                          />
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-12">
                          <label className="form-label">ที่อยู่จัดส่ง</label>
                          <textarea
                            className="form-control"
                            rows="2"
                            value={deliveryAddress}
                            onChange={(e) => setDeliveryAddress(e.target.value)}
                            placeholder="ที่อยู่สำหรับจัดส่งสินค้า (หากต่างจากที่อยู่หลัก)"
                          />
                        </div>
                      </div>

                      {/* Sales Person Information */}
                      <div className="mt-4">
                        <h6 className="mb-3"><i className="fas fa-user-tie me-2"></i>ข้อมูลพนักงานขาย</h6>
                        <div className="row">
                          <div className="col-md-4">
                            <label className="form-label">ชื่อพนักงานขาย</label>
                            <input
                              type="text"
                              className="form-control"
                              value={salesPerson}
                              onChange={(e) => setSalesPerson(e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">เบอร์โทรพนักงานขาย</label>
                            <input
                              type="text"
                              className="form-control"
                              value={salesPhone}
                              onChange={(e) => setSalesPhone(e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">อีเมลพนักงานขาย</label>
                            <input
                              type="email"
                              className="form-control"
                              value={salesEmail}
                              onChange={(e) => setSalesEmail(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Products Section */}
                  <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0"><i className="fas fa-boxes me-2"></i>รายการสินค้า</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary text-white"
                        onClick={addProduct}
                      >
                        <i className="fas fa-plus me-1"></i>เพิ่มสินค้า
                      </button>
                    </div>
                    <div className="card-body">
                      {quotationProducts.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="fas fa-box-open fa-3x mb-3"></i>
                          <div>ยังไม่มีสินค้าในรายการ</div>
                          <div className="small">คลิก "เพิ่มสินค้า" เพื่อเริ่มต้น</div>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th width="15%">ประเภท</th>
                                <th width="35%">รายละเอียด</th>
                                <th width="10%">จำนวน</th>
                                <th width="15%">ราคาต่อหน่วย</th>
                                <th width="15%">รวม</th>
                                <th width="10%">จัดการ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quotationProducts.map((product, index) => (
                                <tr key={index}>
                                  <td>
                                    {product.stockItems && product.stockItems.length > 0 ? (
                                      <span className={`badge ${product.stockItems[0].type === 'battery' ? 'bg-success' : 'bg-info'}`}>
                                        {product.stockItems[0].type === 'battery' ? 'แบตเตอรี่' : 'เครื่องชาร์จ'}
                                      </span>
                                    ) : (
                                      <select
                                        className="form-select form-select-sm"
                                        value={product.type || 'การบริการ'}
                                        onChange={(e) => updateProduct(index, 'type', e.target.value)}
                                      >
                                        <option value="การบริการ">การบริการ</option>
                                        <option value="อะไหล่">อะไหล่</option>
                                      </select>
                                    )}
                                  </td>
                                  <td>
                                    {product.stockItems && product.stockItems.length > 0 ? (
                                      <div>
                                        <div className="fw-medium">{product.stockItems[0].product_name}</div>
                                        {(product.brand || product.stockItems[0].brand) && <div className="text-muted small">ยี่ห้อ: {product.brand || product.stockItems[0].brand}</div>}
                                        {(product.capacity || product.stockItems[0].capacity) && <div className="text-muted small">ขนาด: {product.capacity || product.stockItems[0].capacity} {product.stockItems[0].type === 'battery' ? 'AH' : 'A'}</div>}
                                      </div>
                                    ) : (
                                      <div>
                                        <textarea
                                          className="form-control"
                                          rows="2"
                                          placeholder="รายละเอียดสินค้า"
                                          value={product.description}
                                          onChange={(e) => updateProduct(index, 'description', e.target.value)}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm mt-2"
                                          onClick={() => openStockSelectionModal(index)}
                                        >
                                          <i className="fas fa-boxes me-1"></i>
                                          เลือกจากสต็อก
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      min="1"
                                      value={product.quantity}
                                      onChange={(e) => updateProduct(index, 'quantity', parseInt(e.target.value) || 1)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      min="0"
                                      step="0.01"
                                      value={product.price}
                                      onChange={(e) => updateProduct(index, 'price', parseFloat(e.target.value) || 0)}
                                    />
                                  </td>
                                  <td className="text-end fw-bold">
                                    ฿{((parseFloat(product.price) || 0) * (parseFloat(product.quantity) || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeProduct(index)}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Summary and Notes */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card mb-3">
                        <div className="card-header">
                          <h6 className="mb-0"><i className="fas fa-credit-card me-2"></i>เงื่อนไขการชำระเงิน</h6>
                        </div>
                        <div className="card-body">
                          <textarea
                            className="form-control"
                            rows="3"
                            placeholder="เงื่อนไขการชำระเงิน..."
                            value={paymentTerms}
                            onChange={(e) => setPaymentTerms(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0"><i className="fas fa-tools me-2"></i>เงื่อนไขการบริการ</h6>
                        </div>
                        <div className="card-body">
                          <textarea
                            className="form-control"
                            rows="3"
                            placeholder="เงื่อนไขการบริการ..."
                            value={serviceTerms}
                            onChange={(e) => setServiceTerms(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="card mt-3">
                        <div className="card-header">
                          <h6 className="mb-0"><i className="fas fa-sticky-note me-2"></i>หมายเหตุ</h6>
                        </div>
                        <div className="card-body">
                          <textarea
                            className="form-control"
                            rows="3"
                            placeholder="หมายเหตุเพิ่มเติม..."
                            value={quotationNotes}
                            onChange={(e) => setQuotationNotes(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0"><i className="fas fa-calculator me-2"></i>สรุปยอด</h6>
                        </div>
                        <div className="card-body">
                          {(() => {
                            const totals = calculateTotal();
                            return (
                              <>
                                <div className="row mb-2">
                                  <div className="col-6">รวมสินค้า:</div>
                                  <div className="col-6 text-end">฿{totals.subtotal.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                                </div>
                                <div className="row mb-2">
                                  <div className="col-6">
                                    <label className="form-label mb-0">ส่วนลด (฿):</label>
                                  </div>
                                  <div className="col-6">
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      min="0"
                                      max={totals.subtotal}
                                      step="0.01"
                                      value={discount}
                                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>
                                <div className="row mb-2">
                                  <div className="col-6">ส่วนลด:</div>
                                  <div className="col-6 text-end text-danger">-฿{totals.discountAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                                </div>
                                <div className="row mb-2">
                                  <div className="col-6">ยอดหลังหักส่วนลด:</div>
                                  <div className="col-6 text-end">฿{totals.afterDiscount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                                </div>
                                <div className="row mb-2">
                                  <div className="col-6">ภาษีมูลค่าเพิ่ม 7%:</div>
                                  <div className="col-6 text-end">฿{totals.vatAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                                </div>
                                <hr />
                                <div className="row mb-2">
                                  <div className="col-6"><strong>ยอดรวมสุทธิ:</strong></div>
                                  <div className="col-6 text-end"><strong className="text-primary fs-5">฿{totals.netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong></div>
                                </div>
                                <div className="row">
                                  <div className="col-12 text-center mt-2">
                                    <small className="text-muted">({bahtText(totals.netTotal)})</small>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-top p-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeQuotationForm}
                  >
                    <i className="fas fa-times me-1"></i>
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    className="btn btn-info me-2"
                    onClick={previewQuotationForm}
                    disabled={!selectedCustomer || quotationProducts.length === 0}
                  >
                    <i className="fas fa-eye me-1"></i>
                    พรีวิว
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmitQuotation}
                    disabled={!selectedCustomer || quotationProducts.length === 0}
                  >
                    <i className="fas fa-save me-1"></i>
                    บันทึกใบเสนอราคา
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quotations List */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0"><i className="fas fa-list me-2"></i>รายการใบเสนอราคา</h5>
            </div>
            <div className="card-body">
              {sortedQuotations.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-file-invoice-dollar fa-3x mb-3"></i>
                  <div>ยังไม่มีใบเสนอราคา</div>
                  <div className="small">คลิก "สร้างใบเสนอราคา" เพื่อเริ่มต้น</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th onClick={() => handleSort('quotationNumber')} style={{ cursor: 'pointer' }}>
                          เลขที่ {sortConfig.key === 'quotationNumber' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
                        </th>
                        <th onClick={() => handleSort('customer_name')} style={{ cursor: 'pointer' }}>
                          ลูกค้า {sortConfig.key === 'customer_name' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
                        </th>
                        <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                          วันที่สร้าง {sortConfig.key === 'created_at' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
                        </th>
                        <th onClick={() => handleSort('total_amount')} style={{ cursor: 'pointer' }}>
                          ยอดรวม {sortConfig.key === 'total_amount' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
                        </th>
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                          สถานะ {sortConfig.key === 'status' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
                        </th>
                        <th>ผู้สร้าง</th>
                        <th>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedQuotations.map(quotation => (
                        <tr key={quotation.id}>
                          <td className="fw-bold">{quotation.quotationNumber || `Q${String(quotation.id).padStart(4, '0')}`}</td>
                          <td>
                            <div>{quotation.customer_name}</div>
                            <div className="small text-muted">{quotation.customer_phone}</div>
                          </td>
                          <td>{new Date(quotation.created_at).toLocaleDateString('th-TH')}</td>
                          <td className="text-success fw-bold">฿{quotation.total_amount?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || '0.00'}</td>
                          <td>
                            <span className={`badge ${quotation.status === 'pending' ? 'bg-warning' :
                              quotation.status === 'approved' ? 'bg-success' :
                                quotation.status === 'rejected' ? 'bg-danger' :
                                  quotation.status === 'closed' ? 'bg-primary' : 'bg-secondary'
                              }`}>
                              {quotation.status === 'pending' ? 'รอดำเนินการ' :
                                quotation.status === 'approved' ? 'อนุมัติ' :
                                  quotation.status === 'rejected' ? 'ปฏิเสธ' :
                                    quotation.status === 'closed' ? 'ปิดการขายแล้ว' : quotation.status}
                            </span>
                          </td>
                          <td>
                            <div>{quotation.userEmail || 'ไม่ระบุ'}</div>
                            <div className="small text-muted">{quotation.userPosition || ''}</div>
                          </td>
                          <td>
                            <div className="btn-group" role="group">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => previewQuotation(quotation)}
                                title="พรีวิวใบเสนอราคา"
                              >
                                <i className="fas fa-eye me-1"></i>
                                พรีวิว
                              </button>
                              {quotation.status === 'closed' ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={() => {
                                    // ค้นหาข้อมูลการขายที่เกี่ยวข้อง
                                    const relatedSale = sales.find(s =>
                                      s.quotationNumber === quotation.quotationNumber ||
                                      s.quotation_number === quotation.quotationNumber ||
                                      (s.quotation_id && String(s.quotation_id) === String(quotation.id))
                                    );

                                    if (relatedSale) {
                                      setSelectedSaleForDetails(relatedSale);
                                      setShowSaleDetailsModal(true);
                                    } else {
                                      // ถ้าไม่เจอใน sales context ลองสร้าง object จำลองจาก quotation (fallback)
                                      // หรือแจ้งเตือน
                                      alert('ไม่พบข้อมูลการขายที่เชื่อมโยง กรุณาตรวจสอบในหน้าจัดการการขาย');
                                    }
                                  }}
                                  title="ดูรายละเอียด"
                                >
                                  <i className="fas fa-handshake"></i>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => openSalePopup(quotation)}
                                  title="ปิดการขาย"
                                >
                                  <i className="fas fa-handshake me-1"></i>
                                  ปิดการขาย
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-warning"
                                onClick={() => editQuotation(quotation)}
                                title="แก้ไขใบเสนอราคา"
                                disabled={quotation.status === 'closed'}
                              >
                                <i className="fas fa-edit me-1"></i>
                                แก้ไข
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteQuotation(quotation)}
                                title="ลบใบเสนอราคา"
                                disabled={quotation.status === 'closed'}
                              >
                                <i className="fas fa-trash me-1"></i>
                                ลบ
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sale Confirmation Popup */}
      {showSalePopup && selectedQuotation && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-handshake me-2"></i>
                  ยืนยันการปิดการขาย
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeSalePopup}
                ></button>
              </div>

              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  คุณต้องการปิดการขายสำหรับใบเสนอราคาเลขที่ <strong>{selectedQuotation.quotationNumber || `Q${String(selectedQuotation.id).padStart(4, '0')}`}</strong> หรือไม่?
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <h6>ข้อมูลลูกค้า</h6>
                    <div className="bg-light p-3 rounded">
                      <div><strong>ชื่อ:</strong> {selectedQuotation.customer_name}</div>
                      <div><strong>เบอร์โทร:</strong> {selectedQuotation.customer_phone}</div>
                      <div><strong>ที่อยู่:</strong> {selectedQuotation.customer_address}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>สรุปรายการ</h6>
                    <div className="bg-light p-3 rounded">
                      <div><strong>รายการที่เลือก:</strong> {JSON.parse(selectedQuotation.products || '[]').length} รายการ</div>
                      <div><strong>รายการที่ต้องการ:</strong> {JSON.parse(selectedQuotation.products || '[]').reduce((sum, product) => sum + product.quantity, 0)} ชิ้น</div>
                      <div><strong>ยอดรวม:</strong> <span className="text-success fw-bold">฿{selectedQuotation.total_amount?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || '0.00'}</span></div>
                    </div>
                  </div>
                </div>

                {/* วันที่ส่งมอบ */}
                <div className="row mt-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-truck me-1"></i>
                      วันที่ส่งมอบ
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={saleDeliveryDate}
                      onChange={(e) => setSaleDeliveryDate(e.target.value)}
                    />
                    <small className="text-muted">ใช้คำนวณตาราง PM; ไม่ระบุจะใช้วันขาย</small>
                  </div>
                </div>

                {/* รายการสินค้าและการเลือกซีเรียลนัมเบอร์ */}
                <div className="mt-4">
                  <h6>เลือกซีเรียลนัมเบอร์สำหรับแบตเตอรี่และชาร์จเจอร์</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>สินค้า</th>
                          <th>ยี่ห้อ</th>
                          <th>ความจุ</th>
                          <th>จำนวน</th>
                          <th>ซีเรียลนัมเบอร์</th>
                          <th>ระยะเวลารับประกัน</th>
                          <th>จำนวนครั้ง PM Service</th>
                        </tr>
                      </thead>
                      <tbody>
                        {JSON.parse(selectedQuotation.products || '[]').map((product, index) => {
                          const productBrand = product.brand || (product.stockItems && product.stockItems[0] ? product.stockItems[0].brand : '');
                          const productCapacity = product.capacity || (product.stockItems && product.stockItems[0] ? product.stockItems[0].capacity : '');

                          // ตรวจสอบว่าเป็นบริการทั่วไปหรือไม่ (ไม่มี stockItems หรือ type เป็น 'การบริการ')
                          const isGeneralService = !product.stockItems || product.stockItems.length === 0 || product.type === 'การบริการ';

                          const availableStock = isGeneralService ? [] : [...batteryStock, ...chargerStock].filter(item =>
                            item.status === 'พร้อมขาย' &&
                            item.brand === productBrand &&
                            item.capacity == productCapacity &&
                            ((product.type === 'แบตเตอรี่' && batteryStock.includes(item)) ||
                              (product.type === 'เครื่องชาร์จ' && chargerStock.includes(item)))
                          );

                          return (
                            <tr key={index}>
                              <td>{product.type || 'การบริการ'}</td>
                              <td>{productBrand || '-'}</td>
                              <td>{productCapacity || '-'}</td>
                              <td>{product.quantity}</td>
                              <td>
                                {isGeneralService ? (
                                  <span className="text-muted">ไม่ต้องเลือกซีเรียลนัมเบอร์</span>
                                ) : (
                                  <div className="d-flex flex-wrap gap-1">
                                    {Array.from({ length: product.quantity }, (_, i) => (
                                      <select
                                        key={i}
                                        className="form-select form-select-sm"
                                        style={{ width: '150px' }}
                                        value={selectedSerials[index]?.[i] || ''}
                                        onChange={(e) => {
                                          const newSerials = { ...selectedSerials };
                                          if (!newSerials[index]) newSerials[index] = [];
                                          newSerials[index][i] = e.target.value;
                                          setSelectedSerials(newSerials);
                                        }}
                                      >
                                        <option value="">เลือกซีเรียล...</option>
                                        {availableStock.map(item => (
                                          <option
                                            key={item.serial}
                                            value={item.serial}
                                            disabled={Object.values(selectedSerials).flat().includes(item.serial)}
                                          >
                                            {item.serial}
                                          </option>
                                        ))}
                                      </select>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td>
                                {(product.type === 'แบตเตอรี่' || product.type === 'เครื่องชาร์จ') ? (
                                  <div>
                                    <div className="input-group input-group-sm" style={{ width: '200px' }}>
                                      <input
                                        type="text"
                                        className={`form-control ${warrantyErrors[index] ? 'is-invalid' : ''}`}
                                        placeholder="ระยะเวลา"
                                        value={warrantyPeriods[index] || ''}
                                        onChange={(e) => handleWarrantyPeriodChange(index, e.target.value)}
                                      />
                                      <span className="input-group-text">เดือน</span>
                                    </div>
                                    {warrantyErrors[index] && (
                                      <div className="invalid-feedback d-block">
                                        {warrantyErrors[index]}
                                      </div>
                                    )}
                                    {warrantyPeriods[index] && !warrantyErrors[index] && (
                                      <small className="text-muted">
                                        หมดประกัน: {calculateWarrantyEndDate(warrantyPeriods[index], new Date().toISOString())}
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {(product.type === 'แบตเตอรี่' || product.type === 'เครื่องชาร์จ') ? (
                                  <div>
                                    <div className="input-group input-group-sm" style={{ width: '150px' }}>
                                      <input
                                        type="text"
                                        className={`form-control ${pmServiceErrors[index] ? 'is-invalid' : ''}`}
                                        placeholder="จำนวนครั้ง"
                                        value={pmServiceCounts[index] || ''}
                                        onChange={(e) => handlePmServiceCountChange(index, e.target.value)}
                                      />
                                      <span className="input-group-text">ครั้ง</span>
                                    </div>
                                    {pmServiceErrors[index] && (
                                      <div className="invalid-feedback d-block">
                                        {pmServiceErrors[index]}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeSalePopup}
                >
                  <i className="fas fa-times me-1"></i>
                  ยกเลิก
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleConfirmSale}
                  disabled={isProcessingSale}
                >
                  {isProcessingSale ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      กำลังดำเนินการ...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-1"></i>
                      ยืนยันการปิดการขาย
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal เลือกสินค้าจากสต็อก - ออกแบบใหม่ */}
      {showStockSelectionModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ maxWidth: '85vw', width: '85vw' }}>
            <div className="modal-content" style={{ width: '100%', maxWidth: 'none' }}>
              <div className="modal-header bg-primary text-white">
                <h4 className="modal-title mb-0">
                  <i className="fas fa-shopping-cart me-2"></i>
                  เลือกสินค้าจากสต็อก
                </h4>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeStockSelectionModal}
                ></button>
              </div>

              <div className="modal-body p-4">
                {/* ส่วนการค้นหาและกรองข้อมูล */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-filter me-2"></i>
                      ตัวกรองและค้นหา
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold">
                          <i className="fas fa-search me-1"></i>
                          ค้นหา
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg"
                          placeholder="ค้นหา Serial, ยี่ห้อ, ความจุ..."
                          value={stockSelectionData.searchTerm}
                          onChange={(e) => setStockSelectionData(prev => ({ ...prev, searchTerm: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-bold">
                          <i className="fas fa-tags me-1"></i>
                          ประเภท
                        </label>
                        <select
                          className="form-select form-select-lg"
                          value={stockSelectionData.filterType}
                          onChange={(e) => setStockSelectionData(prev => ({
                            ...prev,
                            filterType: e.target.value,
                            filterBrand: '',
                            filterCapacity: ''
                          }))}
                        >
                          <option value="all">ทั้งหมด</option>
                          <option value="battery">🔋 แบตเตอรี่</option>
                          <option value="charger">⚡ เครื่องชาร์จ</option>
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-bold">
                          <i className="fas fa-industry me-1"></i>
                          ยี่ห้อ
                        </label>
                        <select
                          className="form-select form-select-lg"
                          value={stockSelectionData.filterBrand}
                          onChange={(e) => setStockSelectionData(prev => ({ ...prev, filterBrand: e.target.value }))}
                        >
                          <option value="">ทั้งหมด</option>
                          {getUniqueBrands().map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-bold">
                          <i className="fas fa-battery-half me-1"></i>
                          ความจุ
                        </label>
                        <select
                          className="form-select form-select-lg"
                          value={stockSelectionData.filterCapacity}
                          onChange={(e) => setStockSelectionData(prev => ({ ...prev, filterCapacity: e.target.value }))}
                        >
                          <option value="">ทั้งหมด</option>
                          {getUniqueCapacities().map(capacity => (
                            <option key={capacity} value={capacity}>{capacity}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-lg w-100"
                          onClick={() => setStockSelectionData(prev => ({
                            ...prev,
                            searchTerm: '',
                            filterType: 'all',
                            filterBrand: '',
                            filterCapacity: ''
                          }))}
                          title="ล้างตัวกรอง"
                        >
                          <i className="fas fa-undo me-1"></i>
                          ล้าง
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* สถานะการเลือกสินค้า */}
                <div className="card mb-3 border-success">
                  <div className="card-header bg-gradient" style={{ background: 'linear-gradient(135deg, #28a745, #20c997)' }}>
                    <div className="d-flex justify-content-between align-items-center text-white">
                      <h6 className="mb-0">
                        <i className="fas fa-shopping-cart me-2"></i>
                        สินค้าที่เลือก
                      </h6>
                      <span className="badge bg-light text-dark">
                        {stockSelectionData.selectedStockItems.length} รายการ
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    {stockSelectionData.selectedStockItems.length > 0 ? (
                      <>
                        <div className="row mb-3">
                          <div className="col-md-12">
                            <div className="d-flex align-items-center justify-content-center">
                              <div className="bg-primary rounded-circle p-2 me-3">
                                <i className="fas fa-info-circle text-white"></i>
                              </div>
                              <div>
                                <h6 className="mb-0 text-primary">การเลือกสินค้าสำหรับเสนอราคา</h6>
                                <small className="text-muted">เลือกสินค้าที่ต้องการใส่ในใบเสนอราคา จำนวนและราคาสามารถปรับแก้ได้ภายหลัง</small>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* รายการสินค้าที่เลือก */}
                        <div className="border rounded p-3 bg-light">
                          <h6 className="text-secondary mb-3">
                            <i className="fas fa-list me-2"></i>
                            รายการสินค้าที่เลือก
                          </h6>
                          {stockSelectionData.selectedStockItems.map((item, index) => (
                            <div key={`selected-${item.type}-${item.id}`} className="row align-items-center py-3 border-bottom">
                              <div className="col-md-10">
                                <div className="d-flex align-items-center">
                                  <span className={`badge me-2 ${item.type === 'battery' ? 'bg-success' : 'bg-info'}`}>
                                    {item.type === 'battery' ? '🔋' : '⚡'}
                                  </span>
                                  <div>
                                    <div className="fw-bold">
                                      {item.type === 'battery' ?
                                        `${item.brand} ${item.capacity}Ah` :
                                        `${item.brand} ${item.capacity}A`
                                      }
                                    </div>
                                    <small className="text-muted">
                                      {item.type === 'battery' ? 'แบตเตอรี่' : 'เครื่องชาร์จ'} - สภาพ: {item.condition_status || 'ไม่ระบุ'}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-2">
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    setStockSelectionData(prev => ({
                                      ...prev,
                                      selectedStockItems: prev.selectedStockItems.filter(selected =>
                                        !(selected.id === item.id && selected.type === item.type)
                                      )
                                    }));
                                  }}
                                  title="ลบรายการ"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="text-center mt-3">
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => setStockSelectionData(prev => ({ ...prev, selectedStockItems: [] }))}
                          >
                            <i className="fas fa-trash me-2"></i>
                            ล้างรายการทั้งหมด
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                        <p className="text-muted">ยังไม่มีสินค้าที่เลือก</p>
                        <small className="text-muted">เลือกสินค้าจากตารางด้านล่างเพื่อเริ่มต้น</small>
                      </div>
                    )}
                  </div>
                </div>

                {/* ตารางแสดงข้อมูลสินค้า */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="row align-items-center">
                      <div className="col">
                        <h6 className="mb-0">
                          <i className="fas fa-list me-2"></i>
                          รายการสินค้าในสต็อก ({getFilteredStock().length} รายการ)
                        </h6>
                      </div>
                      <div className="col-auto">
                        <small className="text-muted">
                          คลิกที่แถวเพื่อเลือก/ยกเลิก
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    <table className="table table-hover table-sm">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th width="4%" className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              onChange={(e) => {
                                const filteredStock = getFilteredStock();
                                if (e.target.checked) {
                                  setStockSelectionData(prev => ({
                                    ...prev,
                                    selectedStockItems: [...prev.selectedStockItems, ...filteredStock.filter(item =>
                                      !prev.selectedStockItems.some(selected => selected.id === item.id && selected.type === item.type)
                                    )]
                                  }));
                                } else {
                                  setStockSelectionData(prev => ({
                                    ...prev,
                                    selectedStockItems: prev.selectedStockItems.filter(selected =>
                                      !filteredStock.some(item => item.id === selected.id && item.type === selected.type)
                                    )
                                  }));
                                }
                              }}
                              title="เลือก/ยกเลิกทั้งหมด"
                            />
                          </th>
                          <th width="10%" className="text-center">
                            <i className="fas fa-tags me-1"></i>
                            ประเภท
                          </th>
                          <th width="20%">
                            <i className="fas fa-barcode me-1"></i>
                            <strong>ชื่อสินค้า</strong>
                          </th>
                          <th width="12%">
                            <i className="fas fa-industry me-1"></i>
                            ยี่ห้อ
                          </th>
                          <th width="10%">
                            <i className="fas fa-battery-half me-1"></i>
                            ความจุ
                          </th>
                          <th width="10%" className="text-center">
                            <i className="fas fa-star me-1"></i>
                            สภาพ
                          </th>
                          <th width="15%" className="text-center">
                            <i className="fas fa-check me-1"></i>
                            สถานะ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredStock().map((item, index) => {
                          const isSelected = stockSelectionData.selectedStockItems.some(selected =>
                            selected.id === item.id && selected.type === item.type
                          );

                          return (
                            <tr
                              key={`${item.type}-${item.id}`}
                              className={isSelected ? 'table-success border-success' : 'border-light'}
                              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              onClick={(e) => {
                                if (e.target.type !== 'number' && e.target.type !== 'checkbox') {
                                  toggleStockSelection(item);
                                }
                              }}
                            >
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isSelected}
                                  onChange={() => toggleStockSelection(item)}
                                />
                              </td>
                              <td className="text-center">
                                <span className={`badge fs-6 ${item.type === 'battery' ? 'bg-success' : 'bg-info'}`}>
                                  {item.type === 'battery' ? '🔋 แบตเตอรี่' : '⚡ เครื่องชาร์จ'}
                                </span>
                              </td>
                              <td>
                                <div className="fw-bold text-primary mb-1">
                                  {item.type === 'battery' ?
                                    `${item.brand} ${item.capacity}Ah` :
                                    `${item.brand} ${item.capacity}A`
                                  }
                                </div>
                                <div className="small text-muted">
                                  <i className="fas fa-tag me-1"></i>
                                  {item.type === 'battery' ? 'แบตเตอรี่' : 'เครื่องชาร์จ'}
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-secondary">{item.brand}</span>
                              </td>
                              <td className="text-center">
                                <strong className="text-info">
                                  {item.type === 'battery' ? `${item.capacity} Ah` : `${item.capacity} A`}
                                </strong>
                              </td>
                              <td className="text-center">
                                <span className={`badge ${item.condition_status === 'ใหม่' ? 'bg-success' :
                                  item.condition_status === 'มือสอง' ? 'bg-warning' :
                                    item.condition_status === 'เก่า' ? 'bg-danger' : 'bg-secondary'
                                  }`}>
                                  {item.condition_status || 'ไม่ระบุ'}
                                </span>
                              </td>
                              <td className="text-center">
                                {isSelected ? (
                                  <span className="badge bg-success">
                                    <i className="fas fa-check"></i> เลือกแล้ว
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {getFilteredStock().length === 0 && (
                      <div className="text-center py-4 text-muted">
                        <i className="fas fa-box-open fa-3x mb-3"></i>
                        <div>ไม่พบสินค้าในสต็อกตามเงื่อนไขที่กำหนด</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light border-top">
                <div className="container-fluid">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      {stockSelectionData.selectedStockItems.length > 0 ? (
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="fw-bold text-primary">
                              {stockSelectionData.selectedStockItems.length} รายการ
                            </div>
                            <small className="text-muted">สินค้าที่เลือก</small>
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-info-circle me-2"></i>
                          กรุณาเลือกสินค้าอย่างน้อย 1 รายการ
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 text-end">
                      <button
                        type="button"
                        className="btn btn-outline-secondary me-2"
                        onClick={closeStockSelectionModal}
                      >
                        <i className="fas fa-times me-2"></i>
                        ยกเลิก
                      </button>
                      <button
                        type="button"
                        className={`btn ${stockSelectionData.selectedStockItems.length > 0 ? 'btn-success' : 'btn-secondary'}`}
                        onClick={confirmStockSelection}
                        disabled={stockSelectionData.selectedStockItems.length === 0}
                      >
                        <i className="fas fa-check me-2"></i>
                        {stockSelectionData.selectedStockItems.length > 0 ?
                          `ยืนยันการเลือก (${stockSelectionData.selectedStockItems.length} รายการ)` :
                          'ยืนยันการเลือก'
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sale Confirmation Page */}
      {showSaleConfirmation && saleConfirmationData && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="fas fa-check-circle me-2"></i>
                  ปิดการขายสำเร็จ
                </h5>
              </div>

              <div className="modal-body">
                <div className="alert alert-success">
                  <i className="fas fa-check-circle me-2"></i>
                  <strong>ปิดการขายเรียบร้อยแล้ว!</strong> ข้อมูลการขายได้รับการบันทึกเรียบร้อยแล้ว
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary"><i className="fas fa-user me-2"></i>ข้อมูลลูกค้า</h6>
                    <div className="bg-light p-3 rounded mb-3">
                      <div><strong>ชื่อ:</strong> {saleConfirmationData.quotation.customer_name}</div>
                      <div><strong>เบอร์โทร:</strong> {saleConfirmationData.quotation.customer_phone}</div>
                      <div><strong>ที่อยู่:</strong> {saleConfirmationData.quotation.customer_address}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary"><i className="fas fa-file-invoice me-2"></i>ข้อมูลการขาย</h6>
                    <div className="bg-light p-3 rounded mb-3">
                      <div><strong>เลขที่ใบเสนอราคา:</strong> {saleConfirmationData.quotation.quotationNumber || `Q${String(saleConfirmationData.quotation.id).padStart(4, '0')}`}</div>
                      <div><strong>ยอดรวม:</strong> ฿{Number(saleConfirmationData.quotation.total_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 }) || '0.00'}</div>
                      <div><strong>วันที่ปิดการขาย:</strong> {new Date().toLocaleDateString('th-TH')}</div>
                    </div>
                  </div>
                </div>

                {/* แสดงซีเรียลนัมเบอร์ที่เลือก */}
                <h6 className="text-primary"><i className="fas fa-barcode me-2"></i>ซีเรียลนัมเบอร์ที่เลือก</h6>
                <div className="bg-light p-3 rounded mb-3">
                  {(() => {
                    // ตรวจสอบว่ามีซีเรียลนัมเบอร์ที่เลือกหรือไม่
                    const hasSerials = saleConfirmationData.selectedSerials &&
                      Object.values(saleConfirmationData.selectedSerials).some(serials =>
                        serials && serials.length > 0 && serials.some(s => s && s.trim() !== '')
                      );

                    if (hasSerials) {
                      return (
                        <div className="row">
                          {JSON.parse(saleConfirmationData.quotation.products || '[]').map((product, index) => {
                            const serials = saleConfirmationData.selectedSerials[index];
                            if (!serials || serials.length === 0 || !serials.some(s => s)) return null;

                            return (
                              <div key={index} className="col-md-6 mb-2">
                                <div className="small text-muted">{product.type} - {product.description}</div>
                                <div className="fw-bold">
                                  {serials.filter(s => s).map((serial, idx) => (
                                    <span key={idx} className="badge bg-primary me-1">{serial}</span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else {
                      return <div className="text-muted">ไม่มีซีเรียลนัมเบอร์ (บริการทั่วไป)</div>;
                    }
                  })()}
                </div>

                {/* แสดงระยะเวลารับประกัน */}
                <h6 className="text-primary"><i className="fas fa-shield-alt me-2"></i>ระยะเวลารับประกัน</h6>
                <div className="bg-light p-3 rounded mb-3">
                  {(() => {
                    // ตรวจสอบว่ามีข้อมูลการรับประกันหรือไม่
                    const hasWarranty = saleConfirmationData.warrantyPeriods &&
                      Object.values(saleConfirmationData.warrantyPeriods).some(period =>
                        period && period !== '' && period !== '0'
                      );

                    if (hasWarranty) {
                      return (
                        <div className="row">
                          {JSON.parse(saleConfirmationData.quotation.products || '[]').map((product, index) => {
                            const warrantyMonths = saleConfirmationData.warrantyPeriods[index];
                            if (!warrantyMonths || warrantyMonths === '' || warrantyMonths === '0') return null;

                            return (
                              <div key={index} className="col-md-6 mb-2">
                                <div className="small text-muted">{product.type} - {product.description}</div>
                                <div className="fw-bold text-success">
                                  <i className="fas fa-calendar-alt me-1"></i>
                                  {warrantyMonths} เดือน
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else {
                      return <div className="text-muted">ไม่มีข้อมูลการรับประกัน</div>;
                    }
                  })()}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSaleConfirmation(false);
                    setSaleConfirmationData(null);
                  }}
                >
                  <i className="fas fa-times me-1"></i>
                  ปิด
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => {
                    // ฟังก์ชันสำหรับแก้ไข - จะเพิ่มในอนาคต
                    alert('ฟีเจอร์แก้ไขจะเพิ่มในอนาคต');
                  }}
                >
                  <i className="fas fa-edit me-1"></i>
                  แก้ไข
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quotation Details Modal */}
      <QuotationDetailsModal
        quotation={selectedQuotationForDetails}
        isOpen={showQuotationDetailsModal}
        onClose={() => {
          setShowQuotationDetailsModal(false);
          setSelectedQuotationForDetails(null);
        }}
        onUpdate={() => {
          // รีเฟรชข้อมูลใบเสนอราคาหลังจากอัปเดต
          fetchQuotations();
        }}
      />

      {/* Sale Details Modal */}
      <SaleDetailsModal
        sale={selectedSaleForDetails}
        isOpen={showSaleDetailsModal}
        onClose={() => {
          setShowSaleDetailsModal(false);
          setSelectedSaleForDetails(null);
        }}
        onUpdate={() => {
          // รีเฟรชข้อมูลการขายหลังจากอัปเดต
          // สามารถเพิ่มการรีเฟรชข้อมูลได้ตามต้องการ
        }}
      />
    </div>
  );
};

export default QuotationPage;
